import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

/** The three states a credit note passes through, in order. */
const STATES: { state: string; body: string }[] = [
  {
    state: 'Issued',
    body: 'The note exists as a numbered document with a negative total. Nothing has been settled and no money has moved — but the client already owes less, because accounts receivable nets an unapplied credit against their outstanding balance the moment it is raised.',
  },
  {
    state: 'Applied',
    body: 'The credit is spent against one of the client’s open invoices. That invoice takes a real payment entry and can go Paid, because settling a receivable with credit genuinely clears it. Collected cash does not move: credit-sourced settlements are netted back out of every cash figure, so the month does not report money it never banked.',
  },
  {
    state: 'Refunded',
    body: 'The credit was paid back in cash instead of spent. The note itself is marked paid on the refund date and appears in collected cash as a negative, which is correct — cash really did leave the business. A refunded note is a finished record and offers no further action.',
  },
];

export function CreditNotesGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Give money back with a credit note — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Credit notes</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Give money back, <em>on the record</em>.
        </h1>
        <p className="dp-oneliner">
          A credit note is a numbered document that reduces what a client owes. Raise one against an
          invoice, then either spend it on a receivable or record the cash refund — two separate
          decisions, both audited, and neither of them a quiet edit to an invoice that has already
          gone out.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          What a credit note is
        </h2>
        <p className="dp-plain">
          It is an invoice in every mechanical sense — the same per-client number sequence, the same
          PDF pipeline, the same collection — with a negative total and the word{' '}
          <strong>Credit note</strong> where the header would say Invoice. That is deliberate: your
          receivables total sums what clients owe without needing to know credit notes exist, so an
          unapplied credit reduces the outstanding balance immediately and correctly. Credit notes
          belong to the Finance workspace feature, so they are only available once Finance is enabled
          for your workspace.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          What it is <em>not</em> is a way to correct an invoice. An invoice that has been issued
          keeps its number, its date and its line items for good; the credit note is the second
          document that says how much of it you are giving back, which is what an auditor expects to
          find and what a client can be sent.
        </p>
        <UiPath
          steps={['Finance', 'Invoices', 'open an invoice', 'Issue credit note']}
          note="Also reachable from a client file — Clients, open the client, Billing history, then the invoice."
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Raise one against an invoice
        </h2>
        <p className="dp-plain">
          Give it a reason and one line per thing you are crediting — a description and an amount
          each, so the document explains itself six months later.{' '}
          <strong>Amounts are positive.</strong> You type what you are giving back, which is also
          how you would say it out loud; the negative is the storage detail and Revvly refuses a
          negative input rather than flipping it, because a caller that had already negated would
          otherwise produce a credit note that bills the client.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          The ceiling is what the invoice was raised for, less any credit already raised against it,
          and the form states the headroom up front rather than letting you write out every line and
          then refusing it. The check in the form is a courtesy — a note raised elsewhere while you
          were typing moves the ceiling, and the server&rsquo;s refusal is the one that decides.
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Available from</div>
            <div className="dp-field-v">
              Any issued invoice, paid or not. An over-billed invoice is worth correcting before the
              client pays it as much as after — the only difference is whether the credit settles
              the invoice or comes back as cash, and that is decided later, on the note.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Not available from</div>
            <div className="dp-field-v">
              A credit note (crediting credit is refused) or a held draft, which has no number, no
              amount and nothing to correct yet.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Currency</div>
            <div className="dp-field-v">
              Taken from the invoice or the subscription. A mismatch is refused rather than
              converted — a credit is given back in the currency it was billed in.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Then decide: spend it, or pay it back
        </h2>
        <p className="dp-plain">
          Open the credit note and it offers exactly the two ways a credit leaves the books, for as
          long as there is any left to spend.
        </p>
        <UiPath
          steps={['Finance', 'Invoices', 'open the credit note', 'Apply to an invoice']}
          note="Or Record a refund, alongside it. Both disappear once the note is fully applied or refunded — a spent note is a finished record."
        />
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Apply to an invoice</div>
            <div className="dp-field-v">
              Pick one of the client&rsquo;s open receivables. Leave the suggested amount alone and
              Revvly applies the smaller of the credit left and the balance{' '}
              <em>at the moment of the write</em> — not the figure the form read when it opened,
              which matters because a payment can land against the same invoice while you are
              looking at it. Type a smaller figure to apply part of the credit.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Record a refund</div>
            <div className="dp-field-v">
              Records a payment that has already happened. Revvly is not a payment processor: you
              make the refund in your bank or PSP, and this is the entry that reconciles against it
              — which is why it asks for a reference and lets you backdate to the day the money
              actually left. The whole remaining credit goes back; spend part of it first if only
              part is being refunded.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          The three states, and what each does to your numbers
        </h2>
        <Figure
          title="From issued to spent or refunded"
          caption="Issued lowers what the client owes at once; only a refund moves cash."
        >
          <Flow
            steps={[
              {
                title: 'Issued',
                text: 'Receivables net the unapplied credit. No money has moved.',
              },
              {
                fork: [
                  {
                    title: 'Applied',
                    text: 'Settles an open invoice. Collected cash does not move.',
                    tone: 'accent',
                  },
                  {
                    title: 'Refunded',
                    text: 'Paid back in cash. Shows in collected cash as a negative.',
                    tone: 'warn',
                  },
                ],
              },
            ]}
          />
        </Figure>
        <ol className="qs-steps">
          {STATES.map((s, i) => (
            <li className="qs-step" key={s.state}>
              <span className="qs-phase-num" style={{ marginRight: 12 }}>
                {i + 1}
              </span>
              <div>
                <div className="qs-step-label">{s.state}</div>
                <div className="qs-step-detail">{s.body}</div>
              </div>
            </li>
          ))}
        </ol>
        <div className="api-note" style={{ marginTop: 22 }}>
          <span className="api-note-icon" aria-hidden="true">
            i
          </span>
          <div>
            A credit note is never chased. It carries no due date, it is left out of AR aging, and
            dunning skips it — the two collection behaviours that would otherwise follow from
            sharing the invoice document are switched off explicitly.
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Revvly raises some of them for you
        </h2>
        <p className="dp-plain">
          Two paths issue a credit note without anyone opening the form, because in both cases the
          alternative was money quietly disappearing:
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">A mid-cycle downgrade</div>
            <div className="dp-field-v">
              Moving a customer to something cheaper part-way through a period computes a negative
              proration. That is a credit note against the invoice that charged for the period, so
              the customer can spend it on their next bill.{' '}
              <Link to="/docs/guides/mid-cycle-changes">Mid-cycle changes</Link> covers the timing
              choice.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">A refundable prepaid term</div>
            <div className="dp-field-v">
              Cancelling inside a prepaid span whose commitment is Refundable credits the whole
              periods the customer paid for and will not now consume — of the plan and of the
              components paid ahead with it. Only invoices that were actually
              issued count towards it — a cancelled or voided invoice, or one still held as a draft,
              was never money collected and cannot be refunded.{' '}
              <Link to="/docs/guides/billing-schedules">Billing schedules</Link> covers the
              postures.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          The guardrails
        </h2>
        <ul className="dp-connects">
          <li>
            A credit cannot exceed the invoice it credits, counting every earlier credit against
            that invoice.
          </li>
          <li>
            A credit cannot be spent twice. Two applications of the same note racing each other
            cannot overspend it — the credit is reserved before the receivable is settled, and the
            loser of the race changes nothing.
          </li>
          <li>
            A note cannot be refunded and applied for the same money, in either order. The refund is
            conditional on the note&rsquo;s state at the moment of the write, and a refund that has
            been overtaken is refused with a message saying which of the two happened.
          </li>
          <li>
            A retry is safe. Applying the same note to the same invoice twice is recognised rather
            than doubled, on either side of the pair.
          </li>
        </ul>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          Reading them from an integration
        </h2>
        <p className="dp-plain">
          Credit notes live in the same collection as invoices, so they come back from the invoice
          reads rather than a separate endpoint.
        </p>
        <div className="dp-fields-table" style={{ marginTop: 18 }}>
          <div className="dp-field-row">
            <div className="dp-field-k">Public GraphQL</div>
            <div className="dp-field-v">
              A credit note appears in a client&rsquo;s <span className="api-inline">invoices</span>{' '}
              as a row with a negative <span className="api-inline">amount</span> — that sign is how
              you tell one apart today. It nets into{' '}
              <span className="api-inline">paymentSummary</span> automatically, and only its{' '}
              <em>unapplied</em> remainder does: a note partly spent has already settled the invoice
              it was spent on, so counting its face value again would report the client owing less
              than they do. Credit notes contribute nothing to the receivable counts, because those
              count invoices the client owes.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">MCP / the Assistant</div>
            <div className="dp-field-v">
              <span className="api-inline">query_invoices</span> marks each row{' '}
              <span className="api-inline">isCreditNote</span> and reports{' '}
              <span className="api-inline">creditRemaining</span> on the ones that are, so
              &ldquo;what credit does this client still hold&rdquo; is one question.
            </div>
          </div>
        </div>
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>
            <Link to="/docs/concepts/invoice" style={{ color: 'inherit', textDecoration: 'none' }}>
              Invoice concept ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/api/graphql-public"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Public GraphQL API ›
            </Link>
          </li>
          <li>
            <Link to="/docs/api/mcp" style={{ color: 'inherit', textDecoration: 'none' }}>
              Connect Claude with MCP ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
